const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

export interface BranchUser {
    id: number;
    name: string;
    email: string;
    type: string;
    status: number;
    created_at: string;
}

export interface Branch {
    id: number;
    name: string;
    code: string;
    type: string;
    city_id: number;
    city?: City;
    status: number;
    created_at: string;
    updated_at: string;
    users?: BranchUser[];
}

export interface City {
    id: number;
    name: string;
    code: string;
    zone?: string;
}

export interface CreateBranchData {
    name: string;
    type: string;
    city_id: number;
}

export interface UpdateBranchData {
    name?: string;
    type?: string;
    city_id?: number;
}

export interface BranchesListParams {
    status?: string;
    query?: string;
    city_id?: string;
    page?: number;
    per_page?: number;
}

export interface BranchesListResponse {
    branches: Branch[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

export interface BackendResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

// Helper function to handle API errors
const handleApiError = async (response: Response): Promise<never> => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
};

export const branchApi = {
    // Get branches list with filtering and pagination
    getBranches: async (params: BranchesListParams = {}): Promise<BranchesListResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await fetch(`${API_BASE_URL}/branches?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<BranchesListResponse> = await response.json();
        return data.data;
    },

    // Get single branch details
    getBranch: async (id: number): Promise<Branch> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/branches/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ branch: Branch }> = await response.json();
        return data.data.branch;
    },

    // Create new branch
    createBranch: async (branchData: CreateBranchData): Promise<Branch> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/branches`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(branchData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ branch: Branch }> = await response.json();
        return data.data.branch;
    },

    // Update branch
    updateBranch: async (id: number, branchData: UpdateBranchData): Promise<Branch> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/branches/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(branchData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ branch: Branch }> = await response.json();
        return data.data.branch;
    },

    // Delete branch
    deleteBranch: async (id: number): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/branches/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }
    },

    // Toggle branch status (activate/deactivate)
    toggleBranchStatus: async (id: number): Promise<{ id: number; name: string; status: number }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/branches/${id}/toggle-status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ branch: { id: number; name: string; status: number } }> = await response.json();
        return data.data.branch;
    },

    // Get cities list
    getCities: async (status?: string): Promise<City[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const queryParams = new URLSearchParams();
        if (status !== undefined && status !== null && status !== '') {
            queryParams.append('status', status);
        }

        const url = `${API_BASE_URL}/branches/cities${queryParams.toString() ? `?${queryParams}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ cities: City[] }> = await response.json();
        return data.data.cities;
    },
};
